import React, { useState } from 'react';
import { Text, Box, Flex, Heading, Divider, Button, useBreakpointValue, } from '@spothero/ui';
import { Modal, ModalContent, TimePicker, ModalFooter, } from '@spothero/ui-backlog';
import MultiSelectDatePicker from './MultiSelectDatePicker';
import dayjs, { combineDateAndTime, getAsDayjsTime } from 'utils/dayjs';
import useConfig from 'hooks/use-config';
import { v4 as uuidV4 } from 'uuid';
const PowerBookingModal = ({ onModalClose, onShowResults, selectedDates = [], selectedStartTime = '', selectedEndTime = '', }) => {
    const [startTime, setStartTime] = useState(selectedStartTime);
    const [endTime, setEndTime] = useState(selectedEndTime);
    const [startTimeKey, setStartTimeKey] = useState(uuidV4());
    const [endTimeKey, setEndTimeKey] = useState(uuidV4());
    const [dates, setDates] = useState(selectedDates);
    const showResultsDisabled = !(startTime && endTime && dates.length);
    const { craigApiDateTimeFormat, standardTimeFormat } = useConfig();
    const isMobile = useBreakpointValue({ base: true, tablet: false });
    const handleShowResults = () => {
        const dayjsStartTime = getAsDayjsTime(startTime);
        const dayjsEndTime = getAsDayjsTime(endTime);
        const newPowerBookingPeriods = dates.map(date => {
            const dayjsDate = dayjs(date, 'MM-DD-YYYY');
            return {
                starts: combineDateAndTime(dayjsDate, dayjsStartTime).format(craigApiDateTimeFormat),
                ends: combineDateAndTime(dayjsDate, dayjsEndTime).format(craigApiDateTimeFormat),
            };
        });
        onShowResults(newPowerBookingPeriods);
    };
    const handleStartTimeChange = value => {
        setStartTime(value);
        setEndTimeKey(uuidV4());
        if (endTime &&
            getAsDayjsTime(endTime).isSameOrBefore(getAsDayjsTime(value))) {
            setEndTime(getAsDayjsTime(endTime).add(30, 'm').format(standardTimeFormat));
        }
    };
    const handleEndTimeChange = value => {
        setEndTime(value);
        setStartTimeKey(uuidV4());
    };
    const handleDoneClick = dateSelections => {
        setDates(dateSelections);
    };
    // This avoids users from selecting same start and end time by disabling the time in pickers
    const disableEndTimeBefore = startTime
        ? getAsDayjsTime(startTime).add(30, 'm').format(standardTimeFormat)
        : startTime;
    const disableStartTimeAfter = endTime
        ? getAsDayjsTime(endTime).subtract(30, 'm').format(standardTimeFormat)
        : endTime;
    return (<Modal onCloseClick={onModalClose} data-testid="PowerBookingModal" closeOnShimClick={false} 
    // Could not control certain styles using chakra Box component due to design system V1 limitations
    className="PowerBooking_modal" fullScreenOnMobile>
            <Box data-testid="PowerBookingModal-content" sx={{
            '.Modal-content': {
                px: '4',
                pt: '4',
                mb: '2',
                zIndex: 'layer4',
            },
        }}>
                <ModalContent>
                    <Heading variant="h4" data-testid="PowerBookingModal-heading">
                        Book a spot on multiple days
                    </Heading>
                    <Text mb="4" lineHeight="5" data-testid="PowerBookingModal-helper-text">
                        Looking for a long term parking solution, but don&apos;t
                        need a full month? SpotHero gives you the flexibility to
                        book a spot on the dates of your choice so you&apos;re
                        only paying for what you need.
                    </Text>
                    <Box my="4">
                        <Text fontWeight="semibold" mb="1" data-testid="PowerBookingModal-date-title">
                            Reservation Dates
                        </Text>
                        <MultiSelectDatePicker onDoneClick={handleDoneClick} datesSelected={dates}/>
                    </Box>
                    <Box my="8">
                        <Text fontWeight="semibold" mb="1" data-testid="PowerBookingModal-time-title">
                            Reservation time for all days
                        </Text>
                        <Flex sx={{
            '.TimePicker': {
                width: '250px',
                select: {
                    borderRadius: 'md',
                    fontSize: 'base',
                    ':focus': {
                        borderColor: 'primary.default',
                    },
                },
                '.Select .FormElement-item.FormElement-item-placeholder': {
                    color: 'gray.600',
                },
                '.Select-icon': {
                    color: 'primary.default',
                },
            },
            '.PowerBookingModal-start-time': {
                mr: '4',
            },
        }}>
                            <TimePicker placeholder={isMobile
            ? 'Enter Time'
            : 'Select Enter Time'} data-testid="PowerBookingModal-start-time" className="PowerBookingModal-start-time" onChange={handleStartTimeChange} disableAfter={disableStartTimeAfter} key={startTimeKey} selectedTime={startTime}/>
                            <TimePicker placeholder={isMobile ? 'Exit Time' : 'Select Exit Time'} data-testid="PowerBookingModal-end-time" disableBefore={disableEndTimeBefore} onChange={handleEndTimeChange} key={endTimeKey} selectedTime={endTime}/>
                        </Flex>
                    </Box>
                </ModalContent>
                <Box position={{
            base: 'absolute',
            tablet: 'relative',
            desktop: 'relative',
        }} bottom={{ base: '2' }} width="full" sx={{ '.Modal-footer': { px: '4', mb: '4' } }}>
                    <Divider position={{
            base: 'absolute',
            tablet: 'relative',
            desktop: 'relative',
        }} data-testid="PowerBookingModal-divider"/>
                    <ModalFooter>
                        <Flex mt="4" justify="end" width="full" data-testid="PowerBookingModal-footer">
                            <Button variant="secondary" mr="4" onClick={onModalClose} data-testid="PowerBooking-cancel-button">
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleShowResults} disabled={showResultsDisabled} data-testid="PowerBooking-show-results-button">
                                Show Results
                            </Button>
                        </Flex>
                    </ModalFooter>
                </Box>
            </Box>
        </Modal>);
};
export default PowerBookingModal;
